import type { AssigneeModel, ProcessDraftAssignment, ProcessDraftStep } from '@bliks-io/contracts';
import { getEntityRefId } from '@/lib/entityRefUtils';
import { nodeHasAnyRuleSource, nodeHasSource } from '../edge-actions/shared';

interface SuccessorTargetQuery {
  steps: ProcessDraftStep[];
  assignments: ProcessDraftAssignment[];
  targetModel: AssigneeModel;
  entityId: string;
  parentStable: string;
}

/**
 * Locate the downstream node (step or assignment) that consumes `parentStable`'s
 * task output and is assigned to `entityId`. The successor row's `×` disconnects
 * this node from the parent; the lookup tracks both collections because a
 * freshly-added successor materializes as a draft assignment, while one whose
 * downstream work has been built out lives as a full step. Steps win over
 * assignments when both somehow match. Returns the child's `stableStepId`, or
 * `undefined` when nothing matches (a stale chip is then a no-op).
 */
export function findSuccessorTarget({
  steps,
  assignments,
  targetModel,
  entityId,
  parentStable
}: SuccessorTargetQuery): string | undefined {
  const childStep = steps.find(
    (s) =>
      s.assignedToModel === targetModel &&
      getEntityRefId(s.assignedTo) === entityId &&
      nodeHasSource(s, parentStable)
  );
  if (childStep) return String(childStep.stableStepId);

  const childAssignment = assignments.find(
    (a) =>
      a.assignedToModel === targetModel &&
      getEntityRefId(a.assignedTo) === entityId &&
      nodeHasSource(a, parentStable)
  );
  return childAssignment ? String(childAssignment.stableStepId) : undefined;
}

interface RuleSuccessorTargetQuery extends SuccessorTargetQuery {
  ruleIds: ReadonlySet<string>;
}

/**
 * Rule-scoped analog of {@link findSuccessorTarget}: the downstream node must be
 * bound to one of `ruleIds` (the decision rule's output group) on its edge from
 * the parent. A legacy child may be tagged with any one sibling id in the group,
 * so any match counts.
 */
export function findRuleSuccessorTarget({
  steps,
  assignments,
  targetModel,
  entityId,
  parentStable,
  ruleIds
}: RuleSuccessorTargetQuery): string | undefined {
  const childStep = steps.find(
    (s) =>
      s.assignedToModel === targetModel &&
      getEntityRefId(s.assignedTo) === entityId &&
      nodeHasAnyRuleSource(s, parentStable, ruleIds)
  );
  if (childStep) return String(childStep.stableStepId);

  const childAssignment = assignments.find(
    (a) =>
      a.assignedToModel === targetModel &&
      getEntityRefId(a.assignedTo) === entityId &&
      nodeHasAnyRuleSource(a, parentStable, ruleIds)
  );
  return childAssignment ? String(childAssignment.stableStepId) : undefined;
}
